import { DownloadOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Flex, Input, Modal, Popconfirm, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { ApiError } from '../../lib/api-client';
import { formatDate } from '../../lib/format';
import { useCursorList } from '../../lib/use-cursor-list';
import { confirmSaleReview, exportSaleReviewsCsv, listSaleReviews, rejectSaleReview, type SaleReview } from './api';

const { Text } = Typography;

/**
 * Bike sales to check (spec 2026-10-29): AutoCount sold the bike on a line
 * that names another item code. A person says whether it is this bike's sale;
 * the hourly read never asks again.
 */
export function SaleReviews({ canManage }: { canManage: boolean }): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { rows, isFetching, hasMore, loadMore } = useCursorList(['model-map', 'sale-reviews'], listSaleReviews);
  const [rejecting, setRejecting] = useState<SaleReview | null>(null);
  const [reason, setReason] = useState('');

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['model-map', 'sale-reviews'] });
    void queryClient.invalidateQueries({ queryKey: ['units'] });
  };
  const failed = (e: Error): void => {
    void message.error(e instanceof ApiError ? e.message : t('vehicles.saleReviews.failed'));
    refresh();
  };

  const confirm = useMutation({
    mutationFn: (row: SaleReview) => confirmSaleReview(row.uid),
    onSuccess: (result, row) => {
      void message.success(
        t(result.customer ? 'vehicles.saleReviews.confirmed' : 'vehicles.saleReviews.confirmedNoBuyer', {
          chassis: row.chassisNo,
          docNo: result.docNo,
        }),
      );
      refresh();
    },
    onError: failed,
  });

  const reject = useMutation({
    mutationFn: (input: { row: SaleReview; reason: string }) => rejectSaleReview(input.row.uid, input.reason),
    onSuccess: (_result, input) => {
      void message.success(t('vehicles.saleReviews.rejected', { chassis: input.row.chassisNo }));
      setRejecting(null);
      setReason('');
      refresh();
    },
    onError: failed,
  });

  const download = async (): Promise<void> => {
    try {
      const csv = await exportSaleReviewsCsv();
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bike-sales-to-check.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      failed(e as Error);
    }
  };

  if (rows.length === 0 && !isFetching) return null;

  const bike = (row: SaleReview): ReactNode => (
    <Flex vertical gap={0}>
      <Link to={`/units/${row.unitUid}`} className="tabular">
        {row.chassisNo}
      </Link>
      <Text type="secondary">{[row.bikeItemCode, row.bikeModel].filter((s) => s !== null && s !== '').join(' · ')}</Text>
    </Flex>
  );
  const line = (row: SaleReview): ReactNode => (
    <Flex vertical gap={0}>
      <Text>{row.lineItemCode ?? '—'}</Text>
      {row.lineDescription !== null && <Text type="secondary">{row.lineDescription}</Text>}
    </Flex>
  );
  const invoice = (row: SaleReview): ReactNode => (
    <Flex vertical gap={0}>
      <Text className="tabular">{row.docNo}</Text>
      <Text type="secondary">
        {formatDate(row.soldOn)} · {row.debtorCode}
      </Text>
    </Flex>
  );
  const decide = (row: SaleReview): ReactNode => (
    <Flex gap={8} wrap>
      <Popconfirm
        title={t('vehicles.saleReviews.confirmTitle')}
        description={t('vehicles.saleReviews.confirmWhat', { chassis: row.chassisNo, docNo: row.docNo, debtor: row.debtorCode })}
        okText={t('vehicles.saleReviews.confirm')}
        onConfirm={() => {
          confirm.mutate(row);
        }}
      >
        <Button type="primary" size="large" loading={confirm.isPending && confirm.variables.uid === row.uid}>
          {t('vehicles.saleReviews.confirm')}
        </Button>
      </Popconfirm>
      <Button
        size="large"
        onClick={() => {
          setRejecting(row);
        }}
      >
        {t('vehicles.saleReviews.reject')}
      </Button>
    </Flex>
  );

  return (
    <>
      <ListPage<SaleReview>
        title={t('vehicles.saleReviews.title')}
        loading={isFetching}
        emptyDescription={t('vehicles.saleReviews.empty')}
        rows={rows}
        rowKey={(row) => row.uid}
        hasMore={hasMore}
        onLoadMore={loadMore}
        actions={
          <Button icon={<DownloadOutlined />} onClick={() => void download()}>
            {t('vehicles.saleReviews.export')}
          </Button>
        }
        columns={[
          { title: t('vehicles.saleReviews.bike'), key: 'bike', render: (_v, row) => bike(row) },
          { title: t('vehicles.saleReviews.line'), key: 'line', render: (_v, row) => line(row) },
          { title: t('vehicles.saleReviews.invoice'), key: 'invoice', render: (_v, row) => invoice(row) },
          ...(canManage
            ? [{ title: t('vehicles.saleReviews.decide'), key: 'decide', render: (_v: unknown, row: SaleReview) => decide(row) }]
            : []),
        ]}
        renderMobileRow={(row) => (
          <Flex vertical gap={8}>
            {bike(row)}
            {line(row)}
            {invoice(row)}
            {canManage && decide(row)}
          </Flex>
        )}
      />
      <Modal
        open={rejecting !== null}
        title={t('vehicles.saleReviews.rejectTitle', { chassis: rejecting?.chassisNo ?? '' })}
        okText={t('vehicles.saleReviews.reject')}
        okButtonProps={{ disabled: reason.trim() === '', loading: reject.isPending }}
        onOk={() => {
          if (rejecting !== null) reject.mutate({ row: rejecting, reason: reason.trim() });
        }}
        onCancel={() => {
          setRejecting(null);
          setReason('');
        }}
      >
        <Flex vertical gap={8}>
          <Text>{t('vehicles.saleReviews.rejectWhat', { docNo: rejecting?.docNo ?? '' })}</Text>
          <Input.TextArea
            aria-label={t('vehicles.saleReviews.reason')}
            placeholder={t('vehicles.saleReviews.reasonHint')}
            value={reason}
            maxLength={255}
            autoSize={{ minRows: 2 }}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </Flex>
      </Modal>
    </>
  );
}
